/**
 * Shared editor preview for every Havenlytics block.
 *
 * Combines the three editor-only concerns in one place so all blocks behave
 * consistently:
 *   1. ServerSideRender — the real frontend render (WYSIWYG).
 *   2. EditorPreview — cancels navigation/submits inside the editor.
 *   3. HvnlyPlaceholder — branded placeholder instead of "No Preview" for the
 *      empty / loading / error states.
 *
 * Title and description are read from the registered block type, so callers only
 * pass the block name and attributes.
 *
 * @package
 * @since   3.5.0
 */

import { useSelect } from '@wordpress/data';
import { store as blocksStore } from '@wordpress/blocks';
import ServerSideRender from '@wordpress/server-side-render';

import EditorPreview from './EditorPreview';
import HvnlyPlaceholder from './HvnlyPlaceholder';

/**
 * @param {Object} props
 * @param {string} props.block      Registered block name.
 * @param {Object} props.attributes Block attributes.
 * @return {Element} Guarded, branded server-side preview.
 */
export default function BlockPreview( { block, attributes } ) {
	const blockType = useSelect(
		( select ) => select( blocksStore ).getBlockType( block ),
		[ block ]
	);

	const placeholder = () => (
		<HvnlyPlaceholder
			title={ blockType?.title }
			description={ blockType?.description }
		/>
	);

	// Errors must stay visible: render the real SSR error message instead of
	// masking it behind the branded placeholder.
	const errorPlaceholder = ( { response } ) => (
		<div className="hvnly-block-editor-preview__error">
			<HvnlyPlaceholder
				title={ blockType?.title }
				description={
					response?.errorMsg ||
					'The preview could not be rendered. Check the browser console and PHP error log.'
				}
			/>
		</div>
	);

	return (
		<EditorPreview>
			<ServerSideRender
				block={ block }
				attributes={ attributes }
				EmptyResponsePlaceholder={ placeholder }
				LoadingResponsePlaceholder={ placeholder }
				ErrorResponsePlaceholder={ errorPlaceholder }
			/>
		</EditorPreview>
	);
}
